import { Part, MaterialKey } from './types';
import { MATERIALS } from './materials';

const FORMAT = 'bendsteelcal';
const VERSION = 1;

export interface SavedFile {
  format: typeof FORMAT;
  version: number;
  part: Part;
}

export function serializePart(part: Part): string {
  const payload: SavedFile = { format: FORMAT, version: VERSION, part };
  return JSON.stringify(payload, null, 2);
}

export function parsePartJson(text: string): Part {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (e) {
    throw new Error('File is not valid JSON');
  }
  if (!raw || typeof raw !== 'object') throw new Error('Empty or invalid file');
  const obj = raw as Record<string, unknown>;
  if (obj.format !== FORMAT) throw new Error(`Not a ${FORMAT} file (format=${String(obj.format)})`);
  if (typeof obj.version !== 'number') throw new Error('Missing version');
  if (obj.version > VERSION) throw new Error(`File version ${obj.version} is newer than supported (${VERSION})`);
  const part = obj.part as Part | undefined;
  if (!part || typeof part !== 'object') throw new Error('Missing part');
  validatePart(part);
  // Backward compat: older saves may be missing newer fields.
  if (typeof part.miterGap !== 'number') part.miterGap = 0.4;
  if (!Array.isArray(part.holes)) part.holes = [];
  if (part.dimMode !== 'outside' && part.dimMode !== 'inside') part.dimMode = 'tangent';
  return part;
}

function validatePart(p: Part) {
  if (typeof p.thickness !== 'number' || p.thickness <= 0) throw new Error('Invalid thickness');
  if (typeof p.kFactor !== 'number') throw new Error('Invalid kFactor');
  if (!(p.material in MATERIALS)) throw new Error(`Unknown material "${p.material as MaterialKey}"`);
  if (typeof p.rootId !== 'string') throw new Error('Invalid rootId');
  if (!p.flanges || typeof p.flanges !== 'object') throw new Error('Missing flanges');
  if (!(p.rootId in p.flanges)) throw new Error('Root flange not found in flanges');
  for (const id of Object.keys(p.flanges)) {
    const f = p.flanges[id];
    if (!f || typeof f !== 'object') throw new Error(`Flange ${id} is invalid`);
    if (typeof f.length !== 'number' || typeof f.width !== 'number') {
      throw new Error(`Flange ${id} has invalid dimensions`);
    }
    if (!Array.isArray(f.children)) throw new Error(`Flange ${id} children must be array`);
    for (const c of f.children) {
      if (!(c in p.flanges)) throw new Error(`Flange ${id} references missing child ${c}`);
    }
    if (f.attachment) {
      if (!(f.attachment.parentId in p.flanges)) {
        throw new Error(`Flange ${id} attached to missing parent ${f.attachment.parentId}`);
      }
    }
  }
}

export function downloadJson(filename: string, content: string) {
  const blob = new Blob([content], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function pickJsonFile(): Promise<string> {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,application/json';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) { reject(new Error('No file selected')); return; }
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result ?? ''));
      reader.onerror = () => reject(reader.error ?? new Error('Read failed'));
      reader.readAsText(file);
    };
    input.click();
  });
}
